import uuid from 'uuid';
import {createAction} from 'redux-actions';
import {
  getCurrentDesignId,
  getLastTextIconPosition,
  getProductColorRemovalThreshold,
  getRecentUploadIcons,
  selectedIcon,
  selectedViewId,
  viewUploadedIcons
} from '~/selectors/CanvasSelectors';
import {
  getSelectedProductColor,
  getLaserEngravedDesignColor,
  getView,
  getViewConstraint,
  isAnchorLaserEngraved,
  getDecorationOverrideColor,
  getSelectedProductStyleType
} from '~/selectors/ProductSelectors';
import {getUploadIconData} from '~/selectors/UploadSelectors';
import {
  FILE_EXTENSIONS,
  ACCEPTED_FILE_EXTENSIONS,
  EXT_EXTRACTOR,
  MAXIMUM_FILE_SIZE,
  UNSUPPORTED_IMAGE_TYPES,
  UNSUPPORTED_VIDEO_TYPES,
  PRESIGNED_URL_MAX_FILE_SIZE
} from '~/constants/UploadConstants';
import UploadTypes from '~/constants/types/UploadTypes';
import RemoteServices from '~/constants/RemoteServices';
import {
  topLeftCanvasCoordinates,
  getUploadAutoSizeDimension
} from '~/data/accessors/canvas';
import {deserializeUpload} from '~/data/deserializers/uploadService';
import {difference, makeSpotColor} from '~/utils/colors';
import {setIconDecorationOverrideColors} from '~/utils/icon';
import DesignIconConstants from '~/constants/DesignIconConstants';
import {
  UPLOAD_DIMENSIONS_TOO_LARGE,
  UPLOAD_FILESIZE_TOO_LARGE,
  UPLOAD_FILE_UNSUPPORTED,
  COLOR_CHANGE_PANTONE_WARNING
} from '~/constants/NotificationConstants';
import {uploadColorRemoval} from '~/selectors/ConfigSelectors';
import {
  uploadRemoveColors,
  uploadSuccess as uploadSuccessAnalytics
} from '~/actions/AnalyticsActions';
import {error as errorNotification} from '~/actions/NotificationActions';
import {selectIcon} from '~/actions/SelectionActions';

import {getInkMatches} from '~/selectors/DesignSelectors';
import {metrics} from '~/vendor/metrics';
import {
  isUploadAutoSizingEnabled,
  isUploadFileSizeEnabled
} from '~/selectors/ConfigSelectors';
import {STYLE_TYPE} from '~/constants/ProductConstants';
import rollbar from '~/vendor/rollbar';

export const changeSpotColor =
  ({selected, ...payload}) =>
  (dispatch, getState) => {
    if (selected !== undefined) {
      const state = getState();
      const inkMatches = getInkMatches(state);

      const selectedColor =
        selected?.selectedUser ||
        selected?.user ||
        selected?.palette ||
        selected?.original;

      if (inkMatches?.[selectedColor?.hexString]) {
        return dispatch(
          errorNotification({
            key: COLOR_CHANGE_PANTONE_WARNING,
            attemptedColorChange: {
              colorChange: payload,
              hexString: selectedColor.hexString
            }
          })
        );
      }
    }

    dispatch({
      type: UploadTypes.CHANGE_SPOT_COLOR,
      payload
    });
  };

export const removeSpotColor = createAction(UploadTypes.REMOVE_SPOT_COLOR);

export const addSpotColor =
  ({iconId, color}) =>
  dispatch => {
    const spotColor = makeSpotColor(color);

    dispatch({
      type: UploadTypes.ADD_SPOT_COLOR,
      payload: {
        iconId,
        spotColor
      }
    });
  };

export const invalidFile = createAction(UploadTypes.INVALID_FILE);
export const processingUpload = createAction(UploadTypes.UPLOAD_IN_PROGRESS);
export const resetProgress = createAction(UploadTypes.RESET_PROGRESS);
export const resetUpload = createAction(UploadTypes.RESET_UPLOAD);
export const updateColorMode = createAction(UploadTypes.UPDATE_COLOR_MODE);
export const uploadSuccess = createAction(UploadTypes.UPLOAD_SUCCESS);

export const complete =
  ({uploadIconData}) =>
  (dispatch, getState) => {
    const state = getState();
    const designId = getCurrentDesignId(state);
    const recentUploadCopyId = uuid.v4();
    const view = getView(state, {
      viewIdentifier: uploadIconData.viewId
    });

    const isFirstUpload = viewUploadedIcons(state).size === 0;

    const constraint = getViewConstraint(state)(view);
    const productColor = getSelectedProductColor(state);
    const productStyleType = getSelectedProductStyleType(state);

    const uploadAutoSizing = isUploadAutoSizingEnabled(state);
    const isApparelWithUnsupportedView =
      productStyleType === STYLE_TYPE.APPAREL &&
      !['front', 'back'].includes(view.name);

    let iconData;
    if (uploadAutoSizing && !isApparelWithUnsupportedView) {
      const uploadAutoSizeDimension = getUploadAutoSizeDimension(
        constraint,
        uploadIconData,
        productStyleType,
        isFirstUpload
      );
      iconData = {
        ...uploadAutoSizeDimension,
        isAutoSize: true
      };
    } else {
      iconData = topLeftCanvasCoordinates(constraint);
    }

    const icon = {
      ...uploadIconData,
      ...iconData
    };

    const decorationOverrideColor = getDecorationOverrideColor(state);
    if (decorationOverrideColor || isAnchorLaserEngraved(state)) {
      setIconDecorationOverrideColors({
        iconType: DesignIconConstants.TYPES.upload,
        iconData: icon,
        decorationOverrideColor:
          decorationOverrideColor || getLaserEngravedDesignColor(state)
      });
    }

    if (uploadIconData.uploadAndSwap) {
      icon.selectedIcon = selectedIcon(state);
    }

    const actionType = uploadIconData.uploadAndSwap
      ? UploadTypes.UPLOAD_AND_SWAP
      : UploadTypes.UPLOAD_COMPLETE;

    const uploadColorRemovalFlag = uploadColorRemoval(state);
    if (icon.spotColors) {
      icon.hasColorBelowThreshold = icon.spotColors.some(spotColor => {
        return (
          getProductColorRemovalThreshold(state) >
          difference(spotColor.palette.hexString, productColor.hexString)
        );
      });
      if (icon.hasColorBelowThreshold) {
        dispatch(uploadRemoveColors());
      }
    }

    const layerPosition = getLastTextIconPosition(state);

    dispatch({
      type: actionType,
      payload: {
        designId,
        iconId: uploadIconData.iconId,
        icon,
        layerPosition,
        productColor,
        productColorRemovalThreshold: getProductColorRemovalThreshold(state),
        recentUploadCopyId,
        removeProductColors: uploadColorRemovalFlag
      }
    });
    dispatch(selectIcon({iconId: uploadIconData.iconId}));
    dispatch(resetProgress());

    return true;
  };

export function uploadFailed(error, uploadType = '') {
  return {
    type: UploadTypes.UPLOAD_FAILED,
    payload: {
      error,
      upload_type: uploadType
    }
  };
}

/**
 * Created by @teichmaa in Q2 2025 when working on the presigned URL upload feature.
 * Redux best practices suggest that we should not have side effects in the action creators.
 * Instead, API calls should be handled in middleware, defined in a separate file.
 * Following agile "fast good enough principle", this method is a better separation of concerns than nothing. (Previously, API calls were in the action creator.)
 * Feel free to refactor.
 */
const uploadMiddleware = async ({file, state}) => {
  const formData = new FormData();
  let analyzedDeserializedUpload = null;

  if (isUploadFileSizeEnabled(state)) {
    const getPresignedUrlResponse = await window.fetch(
      `${RemoteServices.upload}/presigned-url`,
      {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type
        })
      }
    );

    if (!getPresignedUrlResponse.ok) {
      throw new Error(UPLOAD_FILE_UNSUPPORTED);
    }

    const getPresignedUrlResponseData = await getPresignedUrlResponse.json();
    const presignedUrl = getPresignedUrlResponseData.url;
    const presignedFields = getPresignedUrlResponseData.fields;

    presignedFields['Content-Type'] = file.type;
    Object.keys(presignedFields).forEach(key => {
      formData.append(key, presignedFields[key]);
    });
    formData.append('file', file);

    const uploadResponse = await window.fetch(presignedUrl, {
      method: 'POST',
      body: formData
    });
    if (!uploadResponse.ok) {
      if (uploadResponse.status === 413) {
        throw new Error(UPLOAD_FILESIZE_TOO_LARGE);
      }
      throw new Error(UPLOAD_FILE_UNSUPPORTED);
    }

    const analyzeResponse = await window.fetch(
      `${RemoteServices.upload}/presigned-url/analysis?key=${presignedFields.key}&gamutMapping=true`,
      {
        method: 'GET',
        headers: {
          'client-name': 'ndx',
          'client-version': import.meta.env.NDX_REVISION
        }
      }
    );
    analyzedDeserializedUpload = deserializeUpload(
      await analyzeResponse.json()
    );
  } else {
    formData.append('upload', file);
    const requestData = {
      method: 'post',
      body: formData,
      credentials: 'omit'
    };
    const gamutUploadUrl =
      RemoteServices.upload + '?' + new URLSearchParams({gamutMapping: 'true'});
    const gamutMappingRequest = window.fetch(gamutUploadUrl, requestData);
    const gamutMappingResponse = await gamutMappingRequest;

    if (!gamutMappingResponse.ok) {
      if (gamutMappingResponse.status === 413) {
        throw new Error(UPLOAD_DIMENSIONS_TOO_LARGE);
      } else {
        throw new Error(UPLOAD_FILE_UNSUPPORTED);
      }
    }

    analyzedDeserializedUpload = deserializeUpload(
      await gamutMappingResponse.json()
    );
  }

  return analyzedDeserializedUpload;
};

export const upload =
  ({file, uploadAndSwap, uploadType}) =>
  async (dispatch, getState) => {
    const state = getState();
    const viewId = selectedViewId(state);
    const iconId = uuid.v4();

    dispatch(
      processingUpload({iconId, viewId, uploadAndSwap, upload_type: uploadType})
    );

    try {
      const analyzedDeserializedUpload = await uploadMiddleware({file, state});

      const futureDateIn60Days = new Date(
        Date.now() + 60 * 24 * 60 * 60 * 1000
      ).getTime();
      analyzedDeserializedUpload.expirationDate =
        new Date().getTime() + futureDateIn60Days;

      dispatch(uploadSuccess(analyzedDeserializedUpload));
      dispatch(
        uploadSuccessAnalytics({
          colorMode: analyzedDeserializedUpload.colorMode,
          colorCount: analyzedDeserializedUpload.spotColors?.size,
          gamutColorCount: analyzedDeserializedUpload.spotColors?.size,
          extension: FILE_EXTENSIONS[file.type] || '',
          upload_type: uploadType
        })
      );

      const uploadIconData = getUploadIconData(getState());
      uploadIconData.iconId = iconId;
      uploadIconData.viewId = viewId;

      dispatch(complete({uploadIconData}));
      return analyzedDeserializedUpload;
    } catch (err) {
      dispatch(uploadFailed(UPLOAD_FILE_UNSUPPORTED));
      rollbar.error(err.message);
    }
    return null;
  };

export const removeProductColors =
  (iconId, removeProductColor) => (dispatch, getState) => {
    const state = getState();
    const productColor = getSelectedProductColor(state);
    if (iconId) {
      dispatch({
        type: UploadTypes.TOGGLE_PRODUCT_COLORS,
        payload: {
          iconId: iconId,
          color: productColor,
          remove: removeProductColor,
          threshold: getProductColorRemovalThreshold(state)
        }
      });
    }
  };

export const getUploadFile = uploadParams => (dispatch, getState) => {
  return window
    .fetch(`${RemoteServices.upload}/${uploadParams.url}`, {
      headers: {
        'client-name': 'ndx',
        'client-version': import.meta.env.NDX_REVISION
      }
    })
    .then(async response => {
      const blob = await response.blob();
      return new File([blob], uploadParams.filename, {
        type: blob.type
      });
    });
};

export const getOriginalUploadContentType = iconId => (dispatch, getState) => {
  if (!iconId) return null;

  const recentUploads = getRecentUploadIcons(getState());
  const recentUpload = recentUploads?.find(upload => upload?.id === iconId);
  const cleanRecentUploadKey = recentUpload?.url?.replace('/uploads/', '');

  return window
    .fetch(`${RemoteServices.upload}/v1/raw/${cleanRecentUploadKey}`, {
      headers: {
        'client-name': 'ndx',
        'client-version': import.meta.env.NDX_REVISION
      }
    })
    .then(response => response?.headers?.get('Content-Type'));
};

export const uploadFile =
  ({
    acceptedFile,
    rejectedFiles = undefined,
    onSuccess = () => {},
    onFailure = () => {},
    onUploadStarted = () => {},
    uploadAndSwap = false,
    uploadType = ''
  }) =>
  async (dispatch, getState) => {
    const state = getState();
    const maxFileSize = isUploadFileSizeEnabled(state)
      ? PRESIGNED_URL_MAX_FILE_SIZE
      : MAXIMUM_FILE_SIZE;

    if (acceptedFile && acceptedFile.size <= maxFileSize) {
      const ext = EXT_EXTRACTOR(acceptedFile.name?.toUpperCase());

      // windows OS does not specify 'type' for .psd files (and maybe some other files out there)
      // Therefore, checking file by its extension initially rather than by its type
      if (!Object.values(ACCEPTED_FILE_EXTENSIONS).includes(`.${ext}`)) {
        let formatType = 'other';

        if (UNSUPPORTED_IMAGE_TYPES.includes(acceptedFile.type)) {
          formatType = 'image';
        } else if (UNSUPPORTED_VIDEO_TYPES.includes(acceptedFile.type)) {
          formatType = 'video';
        }

        metrics.pageViewTag(
          `/ndx/upload/failure/${formatType}${
            ext ? '/' + ext.toLowerCase() : ''
          }`
        );
        dispatch(invalidFile(UPLOAD_FILE_UNSUPPORTED));
        onFailure();
        return;
      }
      onUploadStarted();
      dispatch(upload({file: acceptedFile, uploadAndSwap, uploadType}))
        .then(onSuccess)
        .catch(error => onFailure(error));
    } else if (rejectedFiles?.length) {
      dispatch(uploadFailed(UPLOAD_FILE_UNSUPPORTED, uploadType));
      dispatch(invalidFile(UPLOAD_FILE_UNSUPPORTED));
      onFailure();
    } else if (acceptedFile?.size > maxFileSize) {
      dispatch(uploadFailed(UPLOAD_FILESIZE_TOO_LARGE, uploadType));
      dispatch(invalidFile(UPLOAD_FILESIZE_TOO_LARGE));
      onFailure();
    }
  };
